import { useEffect } from 'react';

export default function SuperbuySpreadsheetSafety() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      'headline': 'Is the Superbuy Spreadsheet Safe? A Complete Trust and Verification Guide',
      'description': 'Everything you need to know about the safety, legitimacy, and trustworthiness of the Superbuy Spreadsheet. Covers link verification, privacy practices, how to spot fake spreadsheets, and why thousands of shoppers trust this organized discovery resource.',
      'author': {
        '@type': 'Person',
        'name': 'Alex Carter',
        'jobTitle': 'Product Research Analyst'
      },
      'datePublished': '2026-07-13',
      'dateModified': '2026-07-13',
      'url': 'https://superbuyspread.ink/is-superbuy-spreadsheet-safe',
      'publisher': {
        '@type': 'Organization',
        'name': 'SuperBuySpread'
      },
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': 'https://superbuyspread.ink/is-superbuy-spreadsheet-safe'
      }
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Is the Superbuy Spreadsheet safe to use for online shopping?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. The Superbuy Spreadsheet is a link directory — it does not collect personal information, process payments, or handle sensitive data. Every category link points directly to official product collection pages on the main platform. The spreadsheet itself has no access to your browsing activity, purchase history, or payment details.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How can I tell if a spreadsheet link is legitimate versus fake?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'All legitimate Superbuy Spreadsheet links direct to the official kaspreadsheet.com domain with recognizable URL structures that include category identifiers. Fake or imitation spreadsheets often use shortened URLs, different domains, or request personal information. Always verify the destination domain before clicking, and only use the spreadsheet from the official superbuyspread.ink website.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Does the Superbuy Spreadsheet collect my personal data or browsing history?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'No. The Superbuy Spreadsheet does not use tracking cookies, does not collect browsing history, and does not require account creation. The only optional data collection is the newsletter email signup in the website footer, which is entirely voluntary and used solely for weekly update notifications.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What should I do if I encounter a broken or suspicious link in the spreadsheet?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Links are verified weekly, but if you encounter a broken link between update cycles, simply return to the Superbuy Spreadsheet homepage and re-enter the category — the link will have been refreshed in the next update. If a link seems suspicious, do not click it and report your concern through the main platform contact channels.'
          }
        },
        {
          '@type': 'Question',
          'name': 'Are there fake Superbuy Spreadsheet websites I should watch out for?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Yes. As the Superbuy Spreadsheet has grown in popularity, imitation sites have appeared that copy the category structure but link to unrelated or potentially harmful destinations. Always ensure you are on superbuyspread.ink — the only official domain. Check the URL bar, look for the consistent visual branding, and avoid any spreadsheet claiming to require login credentials or payment information.'
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <main>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="flex items-center justify-between px-6 md:px-12 py-4 max-w-7xl mx-auto w-full">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-background-50 text-base"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-primary-800 whitespace-nowrap">SuperBuySpread</span>
          </a>
          <div className="flex items-center gap-6">
            <a href="/superbuy-spreadsheet-guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Complete Guide
            </a>
            <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> Home
            </a>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <section className="py-14 md:py-20 px-6 md:px-12 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-medium tracking-widest uppercase text-accent-600 bg-accent-100/60 px-3 py-1 rounded-full whitespace-nowrap">Trust & Safety</span>
            <span className="text-xs text-foreground-400">Updated July 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            Is the Superbuy Spreadsheet Safe? Your Complete Trust Guide
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mt-4 leading-relaxed">
            Understand exactly how the Superbuy Spreadsheet works, how links are verified, what data is — and is not — collected, and how to confidently navigate the platform without security concerns.
          </p>
          <div className="flex items-center gap-4 mt-8 pt-6 border-t border-background-200/60">
            <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
              <span className="text-sm font-semibold text-secondary-700">AC</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Alex Carter</p>
              <p className="text-xs text-foreground-500">Product Research Analyst</p>
            </div>
            <span className="ml-auto text-xs text-foreground-400">6 min read</span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-12 md:py-16 px-6 md:px-12 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {/* Trust signals row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <div className="bg-background-100 rounded-2xl p-5 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-shield-check-line text-secondary-600 text-lg"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">No Login Required</p>
              <p className="text-xs text-foreground-500 mt-1">Use instantly without an account</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-5 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-lock-line text-secondary-600 text-lg"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">No Data Collection</p>
              <p className="text-xs text-foreground-500 mt-1">Browsing activity is never tracked</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-5 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                <i className="ri-refresh-line text-secondary-600 text-lg"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">Weekly Link Verification</p>
              <p className="text-xs text-foreground-500 mt-1">Every link checked every week</p>
            </div>
          </div>

          {/* Direct Answer */}
          <div className="bg-background-100 border border-background-200/60 rounded-2xl p-6 mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-secondary-600 mb-2">The Short Answer</p>
            <p className="text-sm font-semibold text-foreground-900 mb-1">Is the Superbuy Spreadsheet legitimate and safe?</p>
            <p className="text-sm text-foreground-600 leading-relaxed">
              <strong>Yes, completely.</strong> The Superbuy Spreadsheet is fundamentally a link directory — a carefully organized collection of URLs that point to product pages on the main platform (kaspreadsheet.com). It is not a marketplace, does not process payments, does not store credentials, and requires zero personal information to use. You browse categories, click links, and land on the same official product pages you would reach through any other navigation method. The spreadsheet adds organization; it does not insert itself into your transaction or data flow.
            </p>
          </div>

          {/* Section 1 */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            How the Superbuy Spreadsheet Actually Works
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            Understanding the technical reality of how the spreadsheet operates removes most safety concerns immediately. Here is the straightforward process that happens when you use any category link on this website:
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">1</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">You click a category link on superbuyspread.ink</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">This is a standard HTML hyperlink — the same technology that has powered the web for decades. There is no script execution, no redirect tracking, and no intermediate server processing your click.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">2</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">Your browser navigates directly to kaspreadsheet.com</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">The link opens in a new browser tab directly on the main platform. The Superbuy Spreadsheet website does not proxy, monitor, or log this navigation. From the moment you click, you are interacting with the main platform independently.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-semibold">3</div>
              <div>
                <h3 className="font-semibold text-foreground-900 text-sm mb-1">You browse and shop on the main platform as normal</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">Product browsing, price checking, adding to cart, and completing purchases all happen on the main platform. The spreadsheet is only involved in the initial discovery step — think of it as a well-organized signpost, not a middleman.</p>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Privacy: What Data the Superbuy Spreadsheet Collects
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            One of the most common questions from new visitors centers on privacy. People are understandably cautious about any website that facilitates shopping — they want to know what information is being gathered and how it is used.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            The answer is refreshingly simple. The Superbuy Spreadsheet <strong>collects nothing beyond what a standard static website collects</strong>. There are no user accounts, no login systems, no tracking cookies beyond basic analytics, and no mechanism to associate your browsing activity across visits. You do not need to provide an email address, create a profile, or accept any terms beyond browsing the website.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            The only optional data collection point is the <a href="/" className="text-secondary-600 hover:text-secondary-700 underline">newsletter subscription form</a> in the website footer. Submitting your email there is entirely voluntary and is used exclusively to send weekly update notifications about new products and category refreshes. The email address is never shared, sold, or used for any other purpose.
          </p>

          {/* Section 3 - Warning signs */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            How to Spot Fake or Imitation Spreadsheets
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            As with any popular online resource, imitation sites inevitably appear. These copycat websites may use similar names, replicate the category structure, or claim to offer &quot;updated versions&quot; of the spreadsheet. Recognizing the warning signs protects you from landing on potentially harmful pages.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Check</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Legitimate Superbuy Spreadsheet</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-900">Warning Sign of a Fake</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Domain</td>
                  <td className="py-3 px-4 text-secondary-700">superbuyspread.ink</td>
                  <td className="py-3 px-4 text-foreground-500">Different domain, misspelled domain, or free hosting URLs</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Link Destinations</td>
                  <td className="py-3 px-4 text-secondary-700">All point to kaspreadsheet.com</td>
                  <td className="py-3 px-4 text-foreground-500">Links go to unknown or shortened URLs</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Login Requests</td>
                  <td className="py-3 px-4 text-secondary-700">Never asks for login credentials</td>
                  <td className="py-3 px-4 text-foreground-500">Prompts for username, password, or account creation</td>
                </tr>
                <tr className="border-b border-background-200/60">
                  <td className="py-3 px-4 text-foreground-700 font-medium">Payment Requests</td>
                  <td className="py-3 px-4 text-secondary-700">Never requests payment or financial information</td>
                  <td className="py-3 px-4 text-foreground-500">Asks for credit card numbers or payment details</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-foreground-700 font-medium">Content Updates</td>
                  <td className="py-3 px-4 text-secondary-700">Weekly updates with fresh product links</td>
                  <td className="py-3 px-4 text-foreground-500">Static content that never changes or appears copied</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Safe browsing tips */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Safe Browsing Checklist for Spreadsheet Users
          </h2>
          <div className="bg-secondary-50 border border-secondary-200/60 rounded-2xl p-6 mb-8">
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5 text-base flex-shrink-0"></i>
                <span><strong className="text-foreground-900">Bookmark the official URL.</strong> Save superbuyspread.ink in your browser bookmarks and always access the spreadsheet from there. Do not rely on search results to find the site, as imitation pages can appear in rankings.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5 text-base flex-shrink-0"></i>
                <span><strong className="text-foreground-900">Hover before clicking.</strong> On desktop, hover over any category link to preview the destination URL in your browser status bar. Every legitimate link should show a kaspreadsheet.com domain.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5 text-base flex-shrink-0"></i>
                <span><strong className="text-foreground-900">Never enter credentials.</strong> The Superbuy Spreadsheet will never ask you to log in, create an account, or provide payment details. If any page claiming to be the spreadsheet asks for these, close it immediately.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5 text-base flex-shrink-0"></i>
                <span><strong className="text-foreground-900">Check for the HTTPS lock.</strong> The official site uses HTTPS encryption (the padlock icon in your browser address bar). While this alone does not guarantee legitimacy, its absence is a definite red flag.</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-700">
                <i className="ri-checkbox-circle-line text-secondary-500 mt-0.5 text-base flex-shrink-0"></i>
                <span><strong className="text-foreground-900">Return to the homepage if lost.</strong> If you ever feel uncertain about a page you have landed on, simply go back to <a href="/" className="text-secondary-600 hover:text-secondary-700 underline">superbuyspread.ink</a> and navigate from there. The homepage is your trusted anchor point.</span>
              </li>
            </ul>
          </div>

          {/* FAQ */}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
            Frequently Asked Questions About Safety
          </h2>
          <div className="space-y-4 mb-8">
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Do I need antivirus software to use the Superbuy Spreadsheet?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">The spreadsheet is a standard website composed of HTML links and text content. Standard browser security — which every modern browser already includes — is more than sufficient. You do not need any special software beyond what you already use for everyday web browsing.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: Can the spreadsheet infect my device with malware?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">No. The Superbuy Spreadsheet does not host downloadable files, executable scripts, or any content that could deliver malware. It is a static website with text and hyperlinks. Malware delivery requires mechanisms the spreadsheet simply does not have or use, such as file downloads, browser extensions, or software installations.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: What happens if I accidentally click a broken link?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">A broken link will either show a standard &quot;page not found&quot; error on the main platform, or your browser will display a connection error. Neither scenario poses a security risk. Simply return to the <a href="/" className="text-secondary-600 hover:text-secondary-700 underline">Superbuy Spreadsheet homepage</a> and either try the category again or explore a different one — broken links are rare due to weekly verification, but they are harmless when they do occur.</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
              <h3 className="font-semibold text-foreground-900 text-sm mb-2">Q: How can I verify that a Superbuy Spreadsheet page is authentic?</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">Check three things: (1) The URL bar shows superbuyspread.ink as the domain. (2) The visual design matches — consistent warm neutral color palette, Playfair Display headings, rounded cards. (3) All category links preview as kaspreadsheet.com destinations. Imitation sites rarely replicate all three consistency points accurately.</p>
            </div>
          </div>

          {/* Internal navigation */}
          <div className="mt-14 pt-8 border-t border-background-200/60">
            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground-400">Continue Reading</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href="/superbuy-spreadsheet-guide" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-book-open-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Complete Guide</p>
                    <p className="text-xs text-foreground-500">Master the platform</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/comparison" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-scales-3-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Comparison Guide</p>
                    <p className="text-xs text-foreground-500">Spreadsheet vs alternatives</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a href="/" className="group flex items-center gap-3 bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-background-300/80 transition-all cursor-pointer">
                  <i className="ri-home-4-line text-secondary-500 text-lg"></i>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">Back to Home</p>
                    <p className="text-xs text-foreground-500">Browse all categories</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-secondary-900 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <a href="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-secondary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-background-50 text-xs"></i>
              </div>
              <span className="font-heading text-sm font-semibold text-background-50 whitespace-nowrap">SuperBuySpread</span>
            </a>
            <p className="text-xs text-secondary-500">
              &copy; 2026 SuperBuySpread. <a href="/" className="hover:text-secondary-300 transition-colors">Home</a> &bull; <a href="/comparison" className="hover:text-secondary-300 transition-colors">Comparison</a> &bull; <a href="/is-superbuy-spreadsheet-safe" className="hover:text-secondary-300 transition-colors">Safety</a>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}